import { Service, computed, effect, inject, signal, untracked, type Signal } from '@angular/core'
import type { HttpErrorResponse } from '@angular/common/http'
import { type HttpRequest, type HttpResponse } from '@angular/common/http'
import { Subject, finalize } from 'rxjs'
import { TrackingService } from '@awork/_shared/services/tracking-service/tracking.service'
import { TrackingEvent } from '@awork/_shared/services/tracking-service/events'
import { ZonedDatePipe } from '@awork/_shared/pipes/zoned-date.pipe'
import { apiEndpoint } from '@awork/environments/environment'
import { ApiClient } from '@awork/_shared/services/api-client/ApiClient'
import { UserQuery } from '@awork/features/user/state/user.query'
import { FeatureFlagQuery } from '@awork/_shared/state/feature-flag.query'
import { FeatureFlag } from '@awork/_shared/state/feature-flag.store'
import { SignalService } from '@awork/_shared/services/signal-service/signal.service'
import { BrowserService } from '@awork/_shared/services/browser-service/browser.service'
import { UNDO_MAX_TASKS } from '@awork/_shared/services/undo/undo-http-context'
import { generateGUID } from '@awork/_shared/functions/guid-generator'
import replace from '@awork/_shared/functions/replace'
import { ToastService } from '../toast-service/toast.service'
import { UndoLabelService } from './undo-label.service'
import type { UndoActionType, UndoCaptureMetadata, UndoEntry, UndoResult, UndoSource } from './types'

export const UNDO_WINDOW_MS = 30 * 60 * 1000
const STORAGE_KEY = 'aw-undo-pending'
const ACTION_TYPES: UndoActionType[] = [
  'task.status',
  'task.dates',
  'task.priority',
  'task.assignees',
  'task.batch',
  'generic'
]
const EDITOR_SELECTOR =
  'input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="textbox"], .ProseMirror'
const OVERLAY_SELECTOR = '[role="dialog"], [aria-modal="true"], .cdk-overlay-pane, aw-modal, aw-popup'
const IGNORED_MUTATION =
  /^(?:\/me\/settings|\/me\/stats|\/tracking\/|\/me\/onboarding\/|\/me\/notifications|\/notifications\/|\/liveediting\/|\/accounts\/cookie|\/undo\/|\/me\/filters\/.+\/drafts)|negotiate$/i

@Service()
export class UndoService {
  private readonly apiClient = inject(ApiClient)
  private readonly toastService = inject(ToastService)
  private readonly userQuery = inject(UserQuery)
  private readonly trackingService = inject(TrackingService)
  private readonly featureFlagQuery = inject(FeatureFlagQuery)
  private readonly signalService = inject(SignalService)
  private readonly browserService = inject(BrowserService)
  private readonly undoLabelService = inject(UndoLabelService)

  private readonly currentUser = this.userQuery.queryCurrentUser()
  private readonly isEnabled = this.featureFlagQuery.queryFlag(FeatureFlag.undoActions)
  private readonly entry = signal<UndoEntry | null>(null)
  private gestureId: string = generateGUID()
  private registeredGestureId: string | null = null
  private ignoredGroupId: string | null = null
  private floatingActionsVisible: Signal<boolean> = signal(true)

  readonly pending = computed(() => this.visibleEntry())
  readonly isBusy = signal(false)
  readonly completed$ = new Subject<{ mode: 'undo' | 'redo'; taskIds: string[] }>()

  /** Restores this tab's entry and expires it at the original deadline. */
  constructor() {
    effect(() => this.restoreForCurrentUser())
    effect(onCleanup => this.scheduleExpiry(this.entry(), onCleanup))
  }

  /**
   * Uses the framework's visibility signal without injecting its component-scoped service.
   * @param {Signal<boolean>} visible - Whether the floating bar is visible.
   */
  registerFloatingActionsVisibility(visible: Signal<boolean>): void {
    this.floatingActionsVisible = visible
  }

  /**
   * Limits the visible entry to the enabled feature and its owner.
   * @withSignal {Signal<UndoEntry | null>} entry - The stored entry.
   * @withSignal {Signal<boolean>} isEnabled - The workspace flag.
   * @withSignal {Signal<User | null>} currentUser - The signed-in user.
   * @returns {UndoEntry | null} The available entry for the current user.
   */
  private visibleEntry(): UndoEntry | null {
    const entry = this.entry()

    return this.isEnabled() && entry?.userId === this.currentUser()?.id ? entry : null
  }

  /**
   * Waits for a signed-in user before reading this tab's stored entry.
   */
  private restoreForCurrentUser(): void {
    const userId = this.currentUser()?.id

    if (userId) {
      untracked(() => this.restore(userId))
    }
  }

  /**
   * Clears an entry at its original expiry and cancels the previous timer.
   * @param {UndoEntry | null} entry - The current stored entry.
   * @param {(cleanup: () => void) => void} onCleanup - The owning effect's cleanup registration.
   */
  private scheduleExpiry(entry: UndoEntry | null, onCleanup: (cleanup: () => void) => void): void {
    if (!entry) {
      return
    }

    const remaining = UNDO_WINDOW_MS - (Date.now() - entry.firstRegisteredAt)
    const timer = setTimeout(() => this.setEntry(null), Math.max(0, remaining))

    onCleanup(() => clearTimeout(timer))
  }

  /**
   * Returns the id shared by requests from the current gesture.
   * @returns {string} The current gesture id.
   */
  currentGestureId(): string {
    return this.gestureId
  }

  /** Starts a group for the next pointer or keyboard gesture. */
  newGesture(): void {
    this.gestureId = generateGUID()
  }

  /**
   * Links an automatic task update only to an action registered during this gesture.
   * @param {string} taskId - The task receiving the follow-up.
   * @returns {string | null} The current gesture’s pending group id, if one exists.
   */
  groupIdForFollowUp(taskId: string): string | null {
    const entry = this.pending()

    if (entry?.mode === 'undo' && entry.taskIds.includes(taskId) && this.registeredGestureId === this.gestureId) {
      return entry.groupId
    }

    return null
  }

  /**
   * Replaces the pending action or merges another record of its gesture.
   * @param {UndoCaptureMetadata} meta - The response metadata and captured request.
   */
  register(meta: UndoCaptureMetadata): void {
    if (!this.isEnabled() || (meta.groupId !== null && meta.groupId === this.ignoredGroupId)) {
      return
    }

    const previous = this.pending()
    const isSameGroup = previous?.mode === 'undo' && meta.groupId !== null && previous.groupId === meta.groupId
    const taskIds = [...new Set([...(isSameGroup ? previous.taskIds : []), ...this.requestTaskIds(meta.request)])]

    if (taskIds.length > UNDO_MAX_TASKS) {
      this.ignoredGroupId = meta.groupId
      this.setEntry(null)

      return
    }

    const type = ACTION_TYPES.find(type => type === meta.type) ?? 'generic'
    let primaryType = type

    if (isSameGroup && ACTION_TYPES.indexOf(previous.primaryType) < ACTION_TYPES.indexOf(type)) {
      primaryType = previous.primaryType
    }

    const now = Date.now()
    const entry: UndoEntry = {
      ids: [...new Set([...(isSameGroup ? previous.ids : []), meta.id])],
      groupId: meta.groupId,
      primaryType,
      taskIds,
      count: taskIds.length || meta.count,
      label: this.undoLabelService.build(primaryType, meta.request, taskIds),
      mode: 'undo',
      firstRegisteredAt: isSameGroup ? previous.firstRegisteredAt : now,
      lastRegisteredAt: now,
      userId: this.currentUser()?.id ?? ''
    }

    this.setEntry(entry)
    this.registeredGestureId = this.gestureId

    if (!this.floatingActionsVisible()) {
      this.toastService.show(replace(q.translations.UndoService.undoLabel, { label: entry.label }), {
        type: 'action',
        actionText: q.translations.UndoService.undo,
        closeDelay: 6000,
        showRepeated: true,
        action: () => {
          if (this.pending() !== entry) {
            this.showInfo(q.translations.UndoService.expired)

            return
          }

          this.undo('toast')
        }
      })
    }
  }

  /**
   * Cancels an older pending action after another successful user change.
   * @param {HttpRequest<unknown>} request - The successful mutation request.
   * @param {HttpResponse<unknown>} response - The completed response.
   */
  noteMutation(request: HttpRequest<unknown>, response: HttpResponse<unknown>): void {
    const entry = this.pending()

    if (
      !entry ||
      !response.ok ||
      !request.url.startsWith(apiEndpoint) ||
      !['POST', 'PUT', 'PATCH', 'DELETE'].includes(request.method)
    ) {
      return
    }

    const path = request.url.slice(apiEndpoint.length).split('?')[0]
    const groupId = request.headers.get('aw-undo-group') ?? this.currentGestureId()

    if (!IGNORED_MUTATION.test(path) && groupId !== entry.groupId) {
      this.setEntry(null)
    }
  }

  /**
   * Handles the platform shortcuts outside editors and open overlays.
   * @param {KeyboardEvent} event - The browser keyboard event.
   */
  handleKeyboardEvent(event: KeyboardEvent): void {
    const mode = this.keyboardDirection(event)
    const entry = this.pending()

    if (!mode || event.defaultPrevented || event.repeat || this.isBusy() || !entry) {
      return
    }

    if (entry.mode !== mode || Date.now() - entry.firstRegisteredAt > UNDO_WINDOW_MS) {
      return
    }

    const target = event.target instanceof Element ? event.target : null
    const active = document.activeElement

    if (this.isKeyboardBlocked(target) || this.isKeyboardBlocked(active)) {
      return
    }

    event.preventDefault()

    if (mode === 'undo') {
      this.undo('keyboard')
    } else {
      this.redo('keyboard')
    }
  }

  /**
   * Keeps native editor undo and blocking dialogs while allowing the task detail surface.
   * @param {Element | null} target - The event target or focused element.
   * @returns {boolean} Whether the focused surface owns the shortcut.
   */
  private isKeyboardBlocked(target: Element | null): boolean {
    if (target?.closest(EDITOR_SELECTOR)) {
      return true
    }

    for (let element = target; element; element = element.parentElement) {
      if (!element.matches(OVERLAY_SELECTOR)) {
        continue
      }

      // ModalService moves the task modal from its component host to document.body.
      const taskModal = element.closest('aw-modal.task-modal')
      const isTaskDetailSurface = taskModal && (element === taskModal || element.parentElement === taskModal)

      if (!isTaskDetailSurface || element.matches('.cdk-overlay-pane, aw-popup')) {
        return true
      }
    }

    return false
  }

  /**
   * Matches logical keys so QWERTZ Y cannot be interpreted as Undo.
   * @param {KeyboardEvent} event - The browser keyboard event.
   * @returns {'undo' | 'redo' | null} The requested direction, if any.
   */
  private keyboardDirection(event: KeyboardEvent): 'undo' | 'redo' | null {
    const key = event.key.toLowerCase()
    const isModifier = event.metaKey || event.ctrlKey

    if (!isModifier || event.altKey) {
      return null
    }

    if (key === 'z') {
      return event.shiftKey ? 'redo' : 'undo'
    }

    if (this.browserService.getOS() !== 'Mac' && event.ctrlKey && !event.shiftKey && key === 'y') {
      return 'redo'
    }

    return null
  }

  /**
   * Undoes the pending gesture when it is still available.
   * @param {UndoSource} source - The initiating control.
   */
  undo(source: UndoSource): void {
    this.execute('undo', source)
  }

  /**
   * Redoes the pending gesture when it is still available.
   * @param {UndoSource} source - The initiating control.
   */
  redo(source: UndoSource): void {
    this.execute('redo', source)
  }

  /**
   * Sends one undo or redo request and owns its state transition.
   * @param {'undo' | 'redo'} mode - The requested direction.
   * @param {UndoSource} source - The initiating control.
   */
  private execute(mode: 'undo' | 'redo', source: UndoSource): void {
    const entry = this.pending()

    if (!entry || entry.mode !== mode || this.isBusy()) {
      return
    }

    if (Date.now() - entry.firstRegisteredAt > UNDO_WINDOW_MS) {
      this.setEntry(null)
      this.showInfo(q.translations.UndoService.expired)

      return
    }

    this.signalService.removeTemporaryExcludedSignals(
      entry.taskIds.flatMap(taskId => [
        { entityName: 'task', entityId: taskId, propertyName: 'taskStatusId' },
        { entityName: 'task', entityId: taskId, propertyName: 'taskStatus' }
      ])
    )
    this.trackingService.trackEvent(mode === 'undo' ? TrackingEvent.undoRequested : TrackingEvent.redoRequested, {
      actionType: entry.primaryType,
      itemCount: entry.count,
      source
    })
    this.isBusy.set(true)

    const suffix = mode === 'redo' ? '/redo' : ''

    this.apiClient
      .post<UndoResult>(`${apiEndpoint}/undo/${entry.ids.at(-1)}${suffix}`, {})
      .pipe(finalize(() => this.isBusy.set(false)))
      .subscribe({
        next: result => {
          this.trackingService.trackEvent(mode === 'undo' ? TrackingEvent.undoFinished : TrackingEvent.redoFinished, {
            actionType: entry.primaryType,
            state: result.state,
            reason: result.reason
          })
          this.showResult(result, entry, mode)
        },
        error: (error: HttpErrorResponse) => {
          this.trackingService.trackEvent(mode === 'undo' ? TrackingEvent.undoFinished : TrackingEvent.redoFinished, {
            actionType: entry.primaryType,
            state: 'error',
            status: error.status
          })

          if (error.status === 404) {
            if (this.pending() === entry) {
              this.setEntry(null)
            }

            this.showInfo(q.translations.UndoService.expired)
          } else {
            this.showInfo(q.translations.UndoService.failed)
          }
        }
      })
  }

  /**
   * Switches mode after success and reports blocked or expired results.
   * @param {UndoResult} result - The server result.
   * @param {UndoEntry} entry - The entry used for this request.
   * @param {'undo' | 'redo'} mode - The requested direction.
   */
  private showResult(result: UndoResult, entry: UndoEntry, mode: 'undo' | 'redo'): void {
    const hasUndone = result.state === 'undone' || result.state === 'alreadyUndone'
    const hasRedone = result.state === 'redone' || result.state === 'alreadyRedone'

    if (hasUndone || hasRedone) {
      if (this.pending() === entry) {
        this.setEntry({ ...entry, mode: hasUndone ? 'redo' : 'undo', lastRegisteredAt: Date.now() })
      }

      this.completed$.next({ mode, taskIds: result.affectedTaskIds })

      if (result.state === 'undone' || result.state === 'redone') {
        const translation = hasUndone ? q.translations.UndoService.undoneLabel : q.translations.UndoService.redoneLabel

        this.toastService.show(replace(translation, { label: entry.label }), {
          type: 'confirmation',
          closeDelay: 4000,
          showRepeated: true
        })
      }

      return
    }

    if (this.pending() === entry) {
      this.setEntry(null)
    }

    if (result.state === 'expired') {
      this.showInfo(q.translations.UndoService.expired)
    } else if (mode === 'redo') {
      this.showInfo(q.translations.UndoService.redoBlocked)
    } else {
      this.showInfo(this.blockedMessage(result))
    }
  }

  /**
   * Builds the reason shown after a blocked undo.
   * @param {UndoResult} result - The blocked result.
   * @returns {string} The translated reason.
   */
  private blockedMessage(result: UndoResult): string {
    const translations = q.translations.UndoService

    if (result.reason === 'changed') {
      const time = new ZonedDatePipe().transform(result.changedOn, 'HH:mm') ?? ''

      if (result.changedBySystem) {
        return replace(translations.blockedChangedBySystem, { time })
      }

      if (result.changedByUserId === this.currentUser()?.id) {
        return replace(translations.blockedChangedByYou, { time })
      }

      const name = result.changedByUserId ? this.userQuery.getUser(result.changedByUserId)?.fullName : undefined

      if (name && time) {
        return replace(translations.blockedChangedBy, { name, time })
      }

      return translations.blockedChanged
    }

    if (result.reason === 'permission') {
      return translations.blockedPermission
    }

    if (result.reason === 'missing') {
      return translations.blockedMissing
    }

    if (result.reason === 'unsupported') {
      return translations.blockedUnsupported
    }

    return translations.failed
  }

  /**
   * Uses the neutral style for expired, blocked and failed operations.
   * @param {string} message - The translated message.
   */
  private showInfo(message: string): void {
    this.toastService.show(message, { type: 'info', showRepeated: true })
  }

  /**
   * Reads task ids from a capture request without another API call.
   * @param {HttpRequest<unknown>} request - The captured request.
   * @returns {string[]} The referenced task ids.
   */
  private requestTaskIds(request: HttpRequest<unknown>): string[] {
    const body = request.body

    if (Array.isArray(body)) {
      const items: unknown[] = body

      const taskIds = items.flatMap(item => {
        if (item && typeof item === 'object' && 'taskId' in item && typeof item.taskId === 'string') {
          return [item.taskId]
        }

        return []
      })

      if (taskIds.length > 0) {
        return taskIds
      }
    }

    if (body && typeof body === 'object') {
      if ('taskIds' in body && Array.isArray(body.taskIds)) {
        return body.taskIds.filter((id: unknown): id is string => typeof id === 'string')
      }

      if ('taskId' in body && typeof body.taskId === 'string') {
        return [body.taskId]
      }
    }

    const match = request.url.match(/\/tasks\/([^/?]+)(?:\/setassignees)?(?:\?|$)/i)

    return match ? [match[1]] : []
  }

  /**
   * Persists each state change; storage failures do not stop the user action.
   * @param {UndoEntry | null} entry - The pending entry, or null to clear it.
   */
  private setEntry(entry: UndoEntry | null): void {
    this.entry.set(entry)

    try {
      if (entry) {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entry))
      } else {
        sessionStorage.removeItem(STORAGE_KEY)
      }
    } catch {
      // Undo remains available in memory when this tab cannot use storage.
    }
  }

  /**
   * Restores a non-expired entry only for its owner.
   * @param {string} userId - The signed-in user.
   */
  private restore(userId: string): void {
    if (this.entry()?.userId === userId) {
      return
    }

    try {
      const stored = sessionStorage.getItem(STORAGE_KEY)
      const entry: UndoEntry | null = stored ? JSON.parse(stored) : null

      if (
        entry?.userId === userId &&
        Date.now() - entry.firstRegisteredAt <= UNDO_WINDOW_MS &&
        Array.isArray(entry.ids) &&
        entry.ids.length > 0
      ) {
        this.entry.set(entry)
      } else {
        this.setEntry(null)
      }
    } catch {
      // A storage error must not block startup or a later capture.
    }
  }
}
